import { useDispatch } from 'react-redux';

import { trimDriverName, trimDriverNameFromSpreadsheet } from './utils';
import {
  useMutation,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { UUID } from '@cobliteam/common-types';

import {
  type Driver,
  type DriverSpreadsheetResult,
  GraphQLDriversRequester,
  type IDriverSpreadsheetRowAPI,
  type NewDriver,
} from '@cobliteam/api-lib';

import { AlertTypes, showAlertActionCreator } from '@cobliteam/alert';

import useUser from '../useUser';

const QUERY_KEY = 'DRIVER';

const graphqlDriverRequester = new GraphQLDriversRequester();

type GetDriversParams = { options?: UseQueryOptions<Driver[]> } | undefined;

const useDriver = () => {
  const { getSession } = useUser();
  const fleetId = getSession().fleet.id;

  const getDrivers = (params?: GetDriversParams) => {
    return useQuery<Driver[]>({
      queryKey: [QUERY_KEY, fleetId],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
      ...params?.options,
    });
  };
  const useSaveDriver = () => {
    const dispatch = useDispatch();

    return useMutation<Driver, unknown, Driver>({
      mutationFn: async (driver) =>
        graphqlDriverRequester.saveDriver(trimDriverName(driver)),
      onError: () =>
        dispatch(
          showAlertActionCreator(
            AlertTypes.ERROR,
            'Falha ao salvar motorista. Tente novamente mais tarde.'
          )
        ),
    });
  };

  const useSaveDrivers = () => {
    const dispatch = useDispatch();

    return useMutation<Driver[], unknown, NewDriver[]>({
      mutationFn: async (drivers) =>
        graphqlDriverRequester.saveDrivers(
          drivers.map((d) => trimDriverName(d))
        ),
      onError: () =>
        dispatch(
          showAlertActionCreator(
            AlertTypes.ERROR,
            'Falha ao salvar motoristas. Tente novamente mais tarde.'
          )
        ),
    });
  };

  const useDriversImport = () => {
    const dispatch = useDispatch();

    return useMutation<
      DriverSpreadsheetResult,
      unknown,
      IDriverSpreadsheetRowAPI[]
    >({
      mutationFn: async (drivers) =>
        graphqlDriverRequester.driversImport(
          drivers.map((d) => trimDriverNameFromSpreadsheet(d))
        ),
      onError: () =>
        dispatch(
          showAlertActionCreator(
            AlertTypes.ERROR,
            'Falha ao Importar motoristas. Tente novamente mais tarde.'
          )
        ),
    });
  };

  const useDeleteDriver = () => {
    const dispatch = useDispatch();

    return useMutation<UUID, unknown, UUID>({
      mutationFn: async (id) => graphqlDriverRequester.deleteDriver(id),
      onError: () =>
        dispatch(
          showAlertActionCreator(
            AlertTypes.ERROR,
            'Falha ao deletar motorista. Tente novamente mais tarde.'
          )
        ),
    });
  };

  return {
    getDrivers,
    useSaveDriver,
    useSaveDrivers,
    useDriversImport,
    useDeleteDriver,
  };
};

export default useDriver;
